import {
  disableAutoPublishHeader,
  getQueryKey,
  queryClient,
  useApi,
  useDeleteQuery,
  useGetBase64ImageQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { useSuspenseQuery } from "@tanstack/react-query"
import demoScreen from "src/App/play/images/demo-screen.png"
import { getOttEndpointListUrl } from "src/queries/playOttEndpoints"
import { getPushEndpointListUrl } from "src/queries/playPushEndpoints"
import { getPoolUrl, usePool } from "./playPools"
import type { InputListParams, InputParams, InputStreamParams, PoolParams } from "./types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/?org=${orgUuid} */
export const getInputUrl = ({ orgUuid, poolUuid, inputUuid }: Required<InputParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/psi/?org=${orgUuid} */
export const getInputPsiUrl = ({ orgUuid, poolUuid, inputUuid }: Required<InputParams>) =>
  `/pools/${poolUuid}/inputs/${inputUuid}/psi/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/inputs/available/?org=${orgUuid} */
export const getInputAvailableListUrl = ({ orgUuid, poolUuid }: Required<PoolParams>) =>
  `/pools/${poolUuid}/inputs/available/?org=${orgUuid}`

/**
 * @returns /pools/${poolUuid}/inputs/?org=${orgUuid}
 * @returns /pools/${poolUuid}/inputs/?org=${orgUuid}&slate.uuid=${slateUuid}
 */
export const getInputListUrl = ({
  orgUuid,
  poolUuid,
  slateUuid,
  source
}: Required<PoolParams> & InputListParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (slateUuid) params.set("slate.uuid", slateUuid)
  if (source) params.set("source", source)
  return `/pools/${poolUuid}/inputs/?${params}`
}

/** @returns /pools/${poolUuid}/inputs/${inputUuid}/thumbnail/?org=${orgUuid} */
export const getIngestInputThumbnailUrl = ({
  orgUuid,
  poolUuid,
  inputUuid
}: Required<InputParams>) => `/pools/${poolUuid}/inputs/${inputUuid}/thumbnail/?org=${orgUuid}`

/** @returns /public/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/thumbnail/?org=${orgUuid} */
export const getLocalInputThumbnailUrl = ({
  orgUuid,
  poolUuid,
  streamUuid,
  inputUuid
}: Required<InputStreamParams>) =>
  `/public/pools/${poolUuid}/inputs/${inputUuid}/streams/${streamUuid}/thumbnail/?org=${orgUuid}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useInputList = (params?: InputListParams) => {
  const { orgUuid, poolUuid, slateUuid, source } = useRouteParams(params)
  const url = getInputListUrl({ orgUuid, poolUuid, slateUuid, source })

  return useGetQuery<api.ott.PaginatedInputV2List>("ott", url, {
    headers: {
      Accept: "application/json;version=2.0"
    }
  })
}

export const useIngestInputThumbnail = (params?: InputParams) => {
  const mergedParams = useRouteParams(params)
  const url = getIngestInputThumbnailUrl(mergedParams)

  return useGetBase64ImageQuery<string>("ott", url)
}

export const useLocalInputThumbnail = (params?: InputStreamParams) => {
  const mergedParams = useRouteParams(params)
  const url = getLocalInputThumbnailUrl(mergedParams)

  return useGetBase64ImageQuery<string>("ott", url)
}

export const useInput = (params?: InputParams) => {
  const { orgUuid, poolUuid, inputUuid } = useRouteParams(params)
  const url = getInputUrl({ inputUuid, orgUuid, poolUuid })

  return useGetQuery<api.ott.InputV2>("ott", url, {
    headers: {
      Accept: "application/json;version=2.0"
    }
  })
}

export const useAddInput = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const url = getInputListUrl({ ...mergedParams })
  const poolUrl = getPoolUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.InputV2>
  type TResponse = api.ott.InputV2

  const headers = { ...disableAutoPublishHeader, Accept: "application/json;version=2.0" }
  return usePostQuery<TRequest, TResponse>("ott", url, [poolUrl, url], { headers })
}

export const useUpdateInput = (params?: InputParams) => {
  const mergedParams = useRouteParams(params)
  const url = getInputUrl({ ...mergedParams })
  const poolUrl = getPoolUrl(mergedParams)
  const listUrl = getInputListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.InputV2PatchRequest>
  type TResponse = api.ott.InputV2

  return usePatchQuery<TRequest, TResponse>("ott", url, [poolUrl, listUrl], {
    headers: {
      Accept: "application/json;version=2.0"
    }
  })
}

export const useDeleteInput = (params?: InputParams, version?: number) => {
  const mergedParams = useRouteParams(params)
  const url = getInputUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)
  const listUrl = getInputListUrl(mergedParams)
  const pushEndpointListUrl = getPushEndpointListUrl(mergedParams)
  const ottEndpointListUrl = getOttEndpointListUrl(mergedParams)

  return useDeleteQuery(
    "ott",
    url,
    [poolUrl, listUrl, pushEndpointListUrl, ottEndpointListUrl],
    version === 2
      ? {
          headers: {
            Accept: "application/json;version=2.0"
          }
        }
      : undefined
  )
}

//--------------------------------------------------------------------------------------------------
// Other mutations
//--------------------------------------------------------------------------------------------------

export const useEnsureDemoScreenInput = (params?: PoolParams) => {
  const { uploadFile } = useApi("upload")
  const { jsonPost } = useApi("ott")
  const mergedParams = useRouteParams(params)
  const { data: pool } = useSuspenseQuery(usePool())
  const url = getInputListUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)

  type TResponse = api.ott.Input

  return {
    mutationFn: async () => {
      if (!pool.inputs?.find(i => i.name === "Demo screen")) {
        const demoScreenImage = await createDemoScreenImage()
        const { imageUuid } = (await uploadFile("/upload/generated_stream/", demoScreenImage)) as {
          imageUuid: string
        }
        const data = {
          name: "Demo screen",
          streams: [{ generated: { imageUuid }, name: "Demo screen #1", type: "generated" }]
        }
        return jsonPost<TResponse>(url, data)
      }
    },
    onSuccess: async (data?: TResponse) => {
      if (data) {
        const inputUrl = getInputUrl({ ...mergedParams, inputUuid: data.uuid })
        queryClient.setQueryData(getQueryKey("ott", inputUrl), data)

        await queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("ott", poolUrl) })
      }
    }
  }
}

//--------------------------------------------------------------------------------------------------
// Helpers
//--------------------------------------------------------------------------------------------------

const createDemoScreenImage = () =>
  fetch(demoScreen)
    .then(res => res.blob())
    .then(blob => new File([blob], "demo_screen_image", { type: "image/png" }))
